import { useState } from 'react';

import { Typography, XIconButton } from '@capsule/consumer-design-system';

import { css } from 'styled-components';

import AppDownloadIconSvg from 'public/AppDownloadIcon.svg';
import { DeepLinkCampaign, DeepLinkChannel, useGetDeeplinkForAppAdoption } from 'src/app/hooks/data/deeplinks';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { setLastShownAppDownloadInterstitialInLocalStorage } from 'src/app/storage/helpers';

import { Progress } from '../../Elements';

import {
  BenefitList,
  BenefitListItem,
  BenefitsContainer,
  BenefitsTitle,
  ButtonGrouping,
  CloseIconContainer,
  AppDownloadIcon,
  AppDownloadPageContainer,
  OpenCapusleAppButton,
  OpaqueBlock,
  StayOnWebButton,
} from './styles';

interface AppDownloadPageProps {
  onCloseClicked: () => void;
}

export const AppDownloadInterstitial = ({ onCloseClicked }: AppDownloadPageProps) => {
  useSegmentEvent('viewHomescreenAppDownloadInterstitial', { shouldTrackOnMount: true });
  const trackEventSelectHomescreenAppDownload = useSegmentEvent('selectHomescreenAppDownloadInterstitial', {
    shouldTrackOnMount: false,
  });

  const dismissAppDownloadPrompt = () => {
    setLastShownAppDownloadInterstitialInLocalStorage();
    onCloseClicked();
  };

  const [shouldShowButtonLoadingSpinner, setShouldShowButtonLoadingSpinner] = useState(false);
  const { data: deeplinkResponse, isLoading: isGetDeepLinkLoading } = useGetDeeplinkForAppAdoption(
    'home',
    DeepLinkCampaign.app_adoption_interstitial,
    DeepLinkChannel.sms
  );

  const isLoading = isGetDeepLinkLoading || shouldShowButtonLoadingSpinner;

  const openDeeplink = () => {
    setLastShownAppDownloadInterstitialInLocalStorage();
    setShouldShowButtonLoadingSpinner(true);
    if (deeplinkResponse?.url) {
      trackEventSelectHomescreenAppDownload();
      window.open(deeplinkResponse?.url, '_self');
      setShouldShowButtonLoadingSpinner(false);
      dismissAppDownloadPrompt();
    }
  };

  return (
    <AppDownloadPageContainer>
      <CloseIconContainer>
        <XIconButton
          css={css`
            color: ${({ theme }) => theme.colors.capsuleGray50};
          `}
          helperText="dismiss app download prompt"
          onClick={dismissAppDownloadPrompt}
        />
      </CloseIconContainer>
      <BenefitsContainer>
        <AppDownloadIcon src={AppDownloadIconSvg} />
        <BenefitsTitle variant={'heading2'}>Capsule is better in the app</BenefitsTitle>
        <BenefitList>
          <BenefitListItem>
            <Typography variant={'body'}>
              <strong>Smarter.</strong> One-touch refills
            </Typography>
          </BenefitListItem>
          <BenefitListItem>
            <Typography variant={'body'}>
              <strong>Simpler.</strong> All your meds in one place
            </Typography>
          </BenefitListItem>
          <BenefitListItem>
            <Typography variant={'body'}>
              <strong>Faster.</strong> Live chat with expert pharmacists
            </Typography>
          </BenefitListItem>
        </BenefitList>
      </BenefitsContainer>
      <ButtonGrouping>
        <OpaqueBlock />
        <OpenCapusleAppButton onClick={openDeeplink} variant="contained-secondary-lg">
          {isLoading ? <Progress color={'white'} size={20} thickness={2} /> : 'Open Capsule App'}
        </OpenCapusleAppButton>
        <StayOnWebButton onClick={dismissAppDownloadPrompt} variant="text-md">
          Stay on web
        </StayOnWebButton>
      </ButtonGrouping>
    </AppDownloadPageContainer>
  );
};
